import { useState } from 'react'

import { cn } from '@/lib/utils'
import type { PartnerDaily } from '@/types/api'

import { num, usd } from './format'
import { Segmented } from './ui'

type Metric = 'earnedCents' | 'usersOnboarded' | 'paidCents'
const METRICS: Array<{ value: Metric; label: string }> = [
  { value: 'earnedCents', label: 'Earned' },
  { value: 'usersOnboarded', label: 'Users' },
  { value: 'paidCents', label: 'Customers paid' },
]

const day = (iso: string) => new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`))

/**
 * Day by day, one measure at a time (one series, one axis): bars from a shared
 * baseline, a recessive top gridline with its value, and the hovered day named
 * above the plot.
 */
export function DailyChart({ daily }: { daily: PartnerDaily[] }) {
  const [metric, setMetric] = useState<Metric>('earnedCents')
  const [hover, setHover] = useState<number | null>(null)
  const values = daily.map((d) => num(d[metric]))
  const max = Math.max(...values, 0)
  const total = values.reduce((a, b) => a + b, 0)
  const fmt = (v: number) => (metric === 'usersOnboarded' ? v.toLocaleString() : usd(v))
  const shown = hover !== null ? daily[hover] : null

  // Geometry in viewBox units: each day is a 10-wide slot, the bar 8 wide (a 2-unit gap).
  const W = Math.max(daily.length, 1) * 10
  const H = 100

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12.5px] text-ink-muted">{shown ? day(shown.date) : `Last ${daily.length} days`}</p>
          <p className="num mt-0.5 text-[20px] font-semibold leading-tight text-ink">{shown ? fmt(num(shown[metric])) : fmt(total)}</p>
        </div>
        <Segmented label="Measure" size="sm" options={METRICS} value={metric} onChange={setMetric} />
      </div>

      {daily.length === 0 ? (
        <p className="mt-4 rounded-field bg-surface-muted px-4 py-8 text-center text-[13px] text-ink-muted">No days to show yet.</p>
      ) : (
        <div className="mt-3">
          <div className="relative h-[150px]">
            <span className="num pointer-events-none absolute right-0 top-0 -translate-y-1/2 bg-surface pl-1.5 text-[10.5px] text-ink-faint">{max > 0 ? fmt(max) : ''}</span>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full overflow-visible" role="img" aria-label={`${METRICS.find((m) => m.value === metric)?.label} per day`} onMouseLeave={() => setHover(null)}>
              <line x1="0" x2={W} y1="0.5" y2="0.5" className="stroke-line" strokeWidth="1" vectorEffect="non-scaling-stroke" strokeDasharray="3 3" />
              <line x1="0" x2={W} y1={H - 0.5} y2={H - 0.5} className="stroke-line-strong" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              {values.map((v, i) => {
                const h = max > 0 ? (v / max) * (H - 4) : 0
                const x = i * 10 + 1
                return (
                  <g key={daily[i]!.date}>
                    {h > 0 && (
                      <path
                        d={roundedTop(x, H - h, 8, h, Math.min(2.5, h))}
                        className={cn('transition-colors', hover === null || hover === i ? 'fill-brand' : 'fill-brand/35')}
                      />
                    )}
                    {/* A full-height hit target, wider than the bar. */}
                    <rect x={i * 10} y={0} width={10} height={H} fill="transparent" onMouseEnter={() => setHover(i)}>
                      <title>{`${day(daily[i]!.date)}: ${fmt(v)}`}</title>
                    </rect>
                  </g>
                )
              })}
            </svg>
          </div>
          <div className="num mt-1.5 flex justify-between text-[10.5px] text-ink-faint">
            <span>{day(daily[0]!.date)}</span>
            {daily.length > 2 && <span>{day(daily[Math.floor(daily.length / 2)]!.date)}</span>}
            <span>{day(daily[daily.length - 1]!.date)}</span>
          </div>
        </div>
      )}
    </div>
  )
}

/** A bar with rounded top corners, anchored square to the baseline. */
function roundedTop(x: number, y: number, w: number, h: number, r: number): string {
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`
}
